import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, MessageSquare, MoreHorizontal, Pencil } from '../../icons';
import { layerConfirm, layerPrompt } from '../../components/LayerPopup/LayerPopup';
import { useAnchoredMenu } from '../../hooks/useAnchoredMenu';
import { useAiChatStore } from '../../store/aiChatStore';
import { AI_HOME_PATH } from '../../lib/ai-menu';
import type { AiConversationSummary } from '../../api/assist-conversations';

/**
 * AI 메뉴 좌측의 **대화 이력** (설계 판 C · 정의서 M2·M4).
 *
 * ★ 시간대로 묶는다 — 오늘 · 어제 · 지난 7일 · 그 이전(M4, 두 대조군 다 그렇다).
 *   날짜별로 잘게 나누면 하루 한 번 쓰는 사람에게는 줄마다 소제목이 붙는다.
 * ★★★ **삭제는 되돌릴 수 없다**(2026-09-09 결정 — 휴지통을 두지 않는다). 그래서 확인 문구가
 *    **그 사실을 먼저 말한다.** 「지웠는데 어딘가 남아 있다」가 개인정보 약속과 부딪히므로
 *    soft delete 도 숨겨 두지 않았다(표에 `deletedAt` 칸이 아예 없다).
 */

/** 오늘 자정 기준으로 며칠 전인가 — 시각이 아니라 «날»로 센다(어제 23시는 어제다). */
function daysAgo(iso: string, now: Date): number {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return Number.POSITIVE_INFINITY;
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const that = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((midnight - that) / 86_400_000);
}

export interface HistoryGroup { label: string; items: AiConversationSummary[] }

/** 목록 → 시간대 묶음. 빈 묶음은 만들지 않는다(소제목만 남는 자리를 안 만든다). */
export function groupConversations(
  items: readonly AiConversationSummary[],
  now: Date = new Date(),
): HistoryGroup[] {
  const buckets: Array<{ label: string; test: (n: number) => boolean; items: AiConversationSummary[] }> = [
    { label: '오늘', test: (n) => n <= 0, items: [] },
    { label: '어제', test: (n) => n === 1, items: [] },
    { label: '지난 7일', test: (n) => n >= 2 && n <= 7, items: [] },
    { label: '그 이전', test: () => true, items: [] },
  ];
  for (const it of items) {
    const n = daysAgo(it.lastMessageAt, now);
    (buckets.find((b) => b.test(n)) ?? buckets[buckets.length - 1]!).items.push(it);
  }
  return buckets.filter((b) => b.items.length > 0).map(({ label, items: xs }) => ({ label, items: xs }));
}

/**
 * 「새 대화」 단추 — **이력과 따로** 뗀 이유 (2026-09-11 사용자 지시).
 *
 * 레일 차례가 **새 대화 → 하위 메뉴 → 이력**이어야 하는데, 예전에는 이 단추와 이력이 한 덩이라
 * 그 사이에 메뉴를 끼울 수 없었다. 그래서 셸이 차례를 정할 수 있게 갈랐다.
 *
 * ★ 누르면 **대화 화면으로 간다** — 업무자동화를 보다가 눌렀는데 화면이 그대로면
 *   「눌렀는데 아무 일도 안 일어난다」가 된다.
 */
export function AiNewChatButton() {
  const navigate = useNavigate();
  const newConversation = useAiChatStore((s) => s.newConversation);
  return (
    <div className="ainav__ctawrap">
      <button
        type="button"
        className="ainav__cta"
        onClick={() => { newConversation(); navigate(AI_HOME_PATH); }}
      >
        <Plus size={15} aria-hidden />
        새 대화
      </button>
    </div>
  );
}

/** 아직 이름이 없는 새 대화의 이름 — 「새 대화 · 9월 11일 14:30」. */
export function newChatLabel(at: Date): string {
  const two = (n: number) => String(n).padStart(2, '0');
  return `새 대화 · ${at.getMonth() + 1}월 ${at.getDate()}일 ${two(at.getHours())}:${two(at.getMinutes())}`;
}

/**
 * 이력 한 줄 — **제 ⋯ 메뉴를 스스로 갖는다**.
 *
 * ⚠ 훅은 반복문 안에서 못 부른다. 그래서 「줄 하나에 메뉴 하나」를 만들려면 **줄이 컴포넌트**여야
 *   한다(대시보드 탭은 «켜진 탭 하나»에만 ⋯ 이 떠서 훅 한 개로 됐지만, 여기는 줄마다 뜬다).
 * ⚠⚠⚠ 메뉴는 **포털로 띄운다** — 레일이 `overflow-y:auto` 라 안에 그리면 세로로 잘려
 *   「눌러도 아무것도 안 나온다」가 된다(대시보드 탭이 겪은 그 함정).
 */
function HistoryRow({
  c, isCurrent, onOpen, onRename, onDelete,
}: {
  c: AiConversationSummary;
  isCurrent: boolean;
  onOpen: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const menu = useAnchoredMenu<HTMLButtonElement>({ width: 168, height: 92 }, { align: 'end' });
  return (
    <div className={`ainav__conv${isCurrent ? ' is-on' : ''}`}>
      <button
        type="button"
        className="ainav__convopen"
        onClick={onOpen}
        aria-current={isCurrent ? 'true' : undefined}
        title={c.title}
      >
        <MessageSquare size={14} aria-hidden />
        <span className="ainav__convnm">{c.title}</span>
      </button>
      {/* 줄 작업은 손이 닿거나 키보드 초점이 들어올 때 보인다(정본 A8 과 같은 계약). */}
      <button
        type="button"
        ref={menu.anchorRef}
        className="ainav__convmore"
        onClick={menu.toggle}
        aria-label={`${c.title} 대화 설정`}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        title="설정"
      >
        <MoreHorizontal size={14} aria-hidden />
      </button>
      {menu.open && menu.pos && createPortal(
        <>
          {/* 바깥을 누르면 닫힌다 — 메뉴만 띄우면 닫을 길이 없다. */}
          <button type="button" className="ainav__rowscrim" aria-label="메뉴 닫기" onClick={menu.close} />
          <div
            className="ainav__rowmenu"
            role="menu"
            aria-label={`${c.title} 대화 설정`}
            style={{ top: menu.pos.top, left: menu.pos.left }}
          >
            <button type="button" role="menuitem" onClick={() => { menu.close(); onRename(); }}>
              <Pencil size={14} aria-hidden />
              이름 변경
            </button>
            <button
              type="button"
              role="menuitem"
              className="is-danger"
              onClick={() => { menu.close(); onDelete(); }}
            >
              <Trash2 size={14} aria-hidden />
              삭제
            </button>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

export function AiHistory() {
  const conversations = useAiChatStore((s) => s.conversations);
  const currentId = useAiChatStore((s) => s.conversationId);
  const loadList = useAiChatStore((s) => s.loadList);
  const open = useAiChatStore((s) => s.open);
  const remove = useAiChatStore((s) => s.remove);
  const rename = useAiChatStore((s) => s.rename);
  const navigate = useNavigate();

  useEffect(() => { void loadList(); }, [loadList]);

  /**
   * 아직 서버에 이름이 없는 «새 대화» — 목록 맨 위에 자리를 잡아 둔다 (2026-09-11 사용자 요청).
   *
   * ★ 대화 줄은 **첫 질문을 보낸 뒤에야** 생긴다(그때 제목을 질문에서 딴다). 그 전까지 목록이
   *   그대로면 「새 대화를 눌렀는데 아무 일도 안 일어난다」로 보인다.
   * ★ 시각은 **새 대화가 시작된 그 순간**을 쓴다 — 매번 다시 세면 줄이 혼자 움직인다.
   */
  const [newAt, setNewAt] = useState<Date | null>(null);
  const wasId = useRef<string | null>(null);
  useEffect(() => {
    if (currentId === null && wasId.current !== null) setNewAt(new Date());
    if (currentId !== null) setNewAt(null);
    wasId.current = currentId;
  }, [currentId]);

  const groups = groupConversations(conversations);

  /** 이름 바꾸기 — 지금 이름을 채워 물어본다(지우고 다시 적게 하지 않는다). */
  async function onRename(c: AiConversationSummary): Promise<void> {
    const next = await layerPrompt('이 대화를 무엇으로 부를까요?', {
      title: '이름 변경', defaultValue: c.title, placeholder: '대화 이름',
    });
    if (next === null) return;
    try { await rename(c.id, next); } catch { /* 못 바꿔도 목록은 그대로다. */ }
  }

  async function onDelete(c: AiConversationSummary): Promise<void> {
    /* ★★★ 확인 문구가 **되돌릴 수 없다는 사실을 먼저** 말한다(정본 F1 · 2026-09-09 결정). */
    const ok = await layerConfirm(
      `「${c.title}」 대화를 삭제할까요?\n주고받은 말이 함께 사라지며, 되돌릴 수 없습니다.`,
    );
    if (!ok) return;
    await remove(c.id);
  }

  return (
    <>
      {/* 아직 이름이 없는 새 대화 — 서버에 줄이 생기면 아래 목록이 그 자리를 잇는다. */}
      {newAt !== null && (
        <div className="ainav__group">
          <div className="ainav__glabel">지금</div>
          <div className="ainav__conv is-on">
            <span className="ainav__convopen ainav__convnew">
              <MessageSquare size={14} aria-hidden />
              <span className="ainav__convnm">{newChatLabel(newAt)}</span>
            </span>
          </div>
        </div>
      )}

      {groups.length === 0 ? (
        /* 「없다」와 「아직 안 물었다」는 같은 말이다 — 여기서는 굳이 가르지 않는다. */
        <p className="ainav__none">지난 대화가 없습니다.</p>
      ) : (
        groups.map((g) => (
          <div className="ainav__group" key={g.label}>
            <div className="ainav__glabel">{g.label}</div>
            {g.items.map((c) => (
              <HistoryRow
                key={c.id}
                c={c}
                isCurrent={c.id === currentId}
                onOpen={() => {
                  /* ★★★ 열기만 하면 **화면이 안 바뀐다** — 업무자동화를 보다가 지난 대화를
                       누르면 대화가 스토어에만 실리고 눈앞은 그대로였다(2026-09-11 지적).
                       이력을 누르는 것은 「그 대화로 «가겠다»」는 뜻이다. */
                  void open(c.id);
                  navigate(AI_HOME_PATH);
                }}
                onRename={() => { void onRename(c); }}
                onDelete={() => { void onDelete(c); }}
              />
            ))}
          </div>
        ))
      )}
    </>
  );
}
